/**
 * Which rail groups the reader has collapsed, persisted.
 *
 * **This is the fourth module allowed to touch `localStorage`, and the split
 * is deliberate.** `src/auth/session-store.ts` holds the session,
 * `src/lib/theme-store.ts` holds one theme name,
 * `src/lib/column-preference-store.ts` holds column layouts, and this holds
 * collapsed group names and can never hold anything else. A collapsed group
 * name is not a credential, so this widens ADR-0003's exemption to a named
 * file rather than relaxing its rule —
 * `docs/decisions/0012-a-collapsed-rail-group-persists-per-browser.md`
 * carries the case, as ADR-0007 asked it to.
 *
 * `.oxlintrc.json` exempts this file for `localStorage` and nothing else, the
 * same narrowing the other three get: `sessionStorage`, `indexedDB`, `caches`,
 * `document.cookie`, every `window.`-prefixed spelling and the computed form
 * all still fail the gate here.
 *
 * **The COLLAPSED groups are stored, not the open ones.** A group opens by
 * default, so a group this list does not name is open — which means a rail
 * that gains a group shows it, rather than hiding it behind a record written
 * before it existed. A renamed or retired group leaves an entry that names
 * nothing, and an entry that names nothing changes no rail.
 *
 * Nothing here trusts what it reads, and neither entry point throws. The key
 * is editable by hand and outlives the rail that wrote it, so a stale or
 * unreadable key must cost the reader their collapsed groups rather than
 * their rail.
 */

/**
 * The only key this module writes.
 *
 * One key for every group rather than one per group, because a reader who
 * clears it clears their choices, and a per-group key leaves an origin full
 * of names nothing reads.
 */
export const NAV_GROUPS_KEY = 'ui-internal.nav-collapsed-groups'

/**
 * The groups this browser holds as collapsed.
 *
 * @returns The collapsed group names, or an empty list when there is nothing
 *   usable to read.
 */
export function readCollapsedGroups(): readonly string[] {
  let parsed: unknown = null
  try {
    // Storage disabled, and a key that was hand-edited or half-written by a
    // tab that died mid-save, all land here and all mean the same thing.
    parsed = JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) ?? '[]')
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) {
    return []
  }

  // A group name is compared against the declared list, so a non-string entry
  // can only ever fail to match. Dropping it here keeps the list a `string[]`
  // without an assertion, which `typescript/no-unsafe-type-assertion` forbids.
  return parsed.filter(entry => typeof entry === 'string')
}

/**
 * Hold the collapsed groups across reloads.
 *
 * @param groups - The group names to store as collapsed.
 * @returns True when the choice was held, false when storage refused it.
 */
export function writeCollapsedGroups(groups: readonly string[]): boolean {
  try {
    localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(groups))
  } catch {
    // A full quota, or storage disabled. The rail still works for this tab.
    return false
  }

  return true
}
